import { useState } from 'react';

import { Card, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import ClaimScreen, { ClaimBar } from '@/james-playground/aa-family-claim/ClaimScreen';
import AskCard from '@/james-playground/aa-family-claim/claim/cards/AskCard';
import { ASK, say, type Ask } from '@/james-playground/aa-family-claim/claim/asks';
import { voiceOf, type ClaimState } from '@/james-playground/aa-family-claim/claim/claim';
import { BEFORE } from '@/james-playground/aa-family-claim/claim/claim.content';
import { openIn, routeOf, statusOf, type Handoff } from '@/james-playground/aa-family-claim/claim/engine';
import { answerAll, skip, type Value } from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { SCENARIO } from '@/james-playground/aa-family-claim/claim/scenarios';

/**
 * Before we start (spec 2): what KareHero already knows, confirmed in a tap;
 * the quiet end-of-life line; who will sign; and protecting the start date.
 * Routing runs after every answer, so a Scottish address or an existing award
 * leaves for its handoff straight away.
 */
// The quiet line comes before the signer: on the quicker route the helper can
// sign under the special-rules option, which only exists once it is answered.
const SEQUENCE = ['about-identity', 'about-address', 'start-award', 'start-quiet', 'signing-signer', 'start-date'];

const SIGNER_DECISION = ['signingForSomeoneElse', 'signerCategory', 'poaOnlineAccount'];

/** The asks still to do here: claiming for herself skips the signer step. */
export const beforeAsks = (claim: ClaimState): Ask[] =>
  SEQUENCE.map((id) => ASK[id]).filter((ask) => {
    if (ask.id === 'signing-signer' && SCENARIO[claim.scenario].selfClaim) return false;
    const live = ask.entries.filter((id) => statusOf(entry(id), claim.facts) !== 'not_applicable');
    // Optional entries (the start date, "talk first") never hold the step open
    // once the required ones are answered.
    // Here the signer step only decides who signs; the signer's own details
    // wait on the hub under Signing and sending.
    const deciding =
      ask.id === 'signing-signer'
        ? live.filter((id) => SIGNER_DECISION.includes(id))
        : live;
    const required = deciding.filter((id) => entry(id).requiredness !== 'optional');
    const relevant = required.length ? required : deciding;
    // Done once nothing is open and nothing waits to be confirmed.
    return relevant.some((id) => {
      const s = statusOf(entry(id), claim.facts);
      return s === 'open' || s === 'proposed' || s === 'conflict';
    });
  });

const BeforeScreen = ({
  claim,
  today,
  onChange,
  onRoute,
  onDone,
  onLeave,
}: {
  claim: ClaimState;
  today: Date;
  onChange: (claim: ClaimState) => void;
  onRoute: (handoff: Handoff) => void;
  onDone: (claim: ClaimState) => void;
  onLeave?: () => void;
}) => {
  const v = voiceOf(claim);
  const [done, setDone] = useState<{ title: string; reply: string }[]>([]);
  // Steps put off for this sitting ("Later"): the hub keeps the reminder.
  const [passed, setPassed] = useState<string[]>([]);
  const remaining = (c: ClaimState, also: string[] = passed) =>
    beforeAsks(c).filter((a) => !also.includes(a.id));
  const [ask] = remaining(claim);

  const next = (after: ClaimState, title: string, reply: string, put?: string) => {
    setDone((d) => [...d, { title, reply }]);
    const nowPassed = put ? [...passed, put] : passed;
    setPassed(nowPassed);
    const route = routeOf(after.facts, today);
    if (route) {
      onChange(after);
      onRoute(route);
      return;
    }
    if (remaining(after, nowPassed).length) onChange(after);
    else onDone(after);
  };

  const submit = (values: Record<string, Value>, reply: string) =>
    next(
      { ...claim, facts: answerAll(claim.facts, values) },
      say(ask.title, v),
      reply,
      Object.keys(values).length ? undefined : ask.id,
    );

  const skipAll = () =>
    next(
      {
        ...claim,
        facts: openIn(ask, claim.facts).reduce((f, e) => skip(f, e.id), claim.facts),
      },
      say(ask.title, v),
      'I’m not sure, we’ll talk it through',
    );

  return (
    <ClaimScreen
      bar={<ClaimBar onLeave={onLeave} />}
      eyebrow={BEFORE.title}
      title={ask ? say(ask.title, v) : BEFORE.title}
      description={done.length ? undefined : BEFORE.intro}
    >
      {done.length > 0 && (
        <Card sx={{ p: 2 }}>
          <Stack component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }} spacing={1}>
            {done.map((d) => (
              <Stack key={d.title} component='li' direction='row' spacing={1.25} sx={{ alignItems: 'flex-start' }}>
                <Stack sx={{ color: 'primary.main', pt: 0.25 }}>
                  <Icon icon={'circle-check' as never} fontSize={14} />
                </Stack>
                <Typography variant='body2'>
                  {d.title}: {d.reply}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}
      {ask && (
        <Stack spacing={1.5}>
          <Typography variant='body1'>{say(ask.prompt, v)}</Typography>
          <AskCard key={ask.id} ask={ask} facts={claim.facts} voice={v} onSubmit={submit} onSkip={skipAll} />
        </Stack>
      )}
    </ClaimScreen>
  );
};

export default BeforeScreen;
